import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

interface Collection {
  id: string;
  title: string;
  description: string | null;
  content: string;
  tags: string[];
  created_at: string;
  updated_at: string;
}

interface CreateCollectionData {
  title: string;
  description?: string;
  content: string;
  tags: string[];
}

export function useCollections() {
  const [collections, setCollections] = useState<Collection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadCollections = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error: fetchError } = await supabase
        .from('collections')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;
      setCollections(data || []);
    } catch (err) {
      console.error('Error loading collections:', err);
      setError(err instanceof Error ? err.message : 'Failed to load collections');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCollections();
  }, []);

  const createCollection = async (data: CreateCollectionData) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error: createError } = await supabase
        .from('collections')
        .insert([{
          ...data,
          user_id: user.id
        }]);

      if (createError) throw createError;
      await loadCollections();
    } catch (err) {
      console.error('Error creating collection:', err);
      throw err;
    }
  };

  const updateCollection = async (id: string, data: Partial<CreateCollectionData>) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error: updateError } = await supabase
        .from('collections')
        .update(data)
        .eq('id', id)
        .eq('user_id', user.id);

      if (updateError) throw updateError;
      await loadCollections();
    } catch (err) {
      console.error('Error updating collection:', err);
      throw err;
    }
  };

  const deleteCollection = async (id: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error: deleteError } = await supabase
        .from('collections')
        .delete()
        .eq('id', id)
        .eq('user_id', user.id);

      if (deleteError) throw deleteError;
      await loadCollections();
    } catch (err) {
      console.error('Error deleting collection:', err);
      throw err;
    }
  };

  return {
    collections,
    loading,
    error,
    createCollection,
    updateCollection,
    deleteCollection,
    refresh: loadCollections
  };
}